import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lightning, ArrowLeft } from "@phosphor-icons/react";

/**
 * Forgot-password screen. Posts the email to /api/auth/forgot-password.
 * Email delivery via Resend is not yet wired up — the backend logs the reset
 * intent and returns a generic response so we cannot leak account existence.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success(data.message || "Check your inbox.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 hidden md:flex items-center justify-center bg-secondary/40 border-r border-border p-16">
        <div className="max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-10" data-testid="fp-brand">
            <Lightning size={22} weight="fill" className="text-primary" />
            <span className="font-display text-xl font-bold">Tradelog</span>
          </Link>
          <h1 className="font-display text-4xl font-bold tracking-tight">Locked out?</h1>
          <p className="mt-4 text-muted-foreground">Enter the email tied to your account and we'll send you a reset link.</p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="fp-form">
          <div>
            <h2 className="font-display text-3xl font-bold">Reset password</h2>
            <p className="text-sm text-muted-foreground mt-1">We'll email a secure link to reset your password.</p>
          </div>

          {sent ? (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-400" data-testid="fp-sent">
              If an account exists for <b>{email}</b>, a reset link is on its way. Please check your inbox — it may take a minute.
              <p className="mt-2 text-xs text-emerald-400/70">
                Note: email delivery is still being wired up. If nothing arrives in 5 minutes, please contact support.
              </p>
            </div>
          ) : (
            <>
              <div>
                <Label>Email</Label>
                <Input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                  data-testid="fp-email" placeholder="you@example.com" />
              </div>
              <Button type="submit" disabled={loading || !email} className="w-full rounded-full" data-testid="fp-submit">
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </>
          )}

          <p className="text-sm text-muted-foreground text-center flex items-center justify-center gap-1.5">
            <ArrowLeft size={13} />
            <Link to="/login" className="text-primary underline" data-testid="fp-back-login">Back to sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
